import {
  DC_Domain_Models_Documents_DocumentType,
  DC_Repositories_Base_Enumerations_BusinessUnit,
  DC_Repositories_Base_Enumerations_Label,
  ResponseModels_Documents_ContractResponseModel,
  ResponseModels_Documents_CustomerDocumentResponse,
} from '@monorepo-types/dc';

import { request } from '../client';
import type { ApiRequestConfig } from '../client/types';

type GetMonthlyEnergyReportDocumentForCustomerByDocumentId = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
  documentId: string;
};
/**
 * Gets the monthly energy report file in an object given a document id and customer id.
 * Uses Digital Core Document system API plugged in through Apigee to Macaw's sharepoint endpoint.
 * @returns Blob If process went ok, body contains the document content.
 */
export function getMonthlyEnergyReportDocumentForCustomerByDocumentId(
  { businessUnit, label, customerId, documentId }: GetMonthlyEnergyReportDocumentForCustomerByDocumentId,
  requestConfig: ApiRequestConfig = {},
): Promise<Blob> {
  return request(
    {
      method: 'GET',
      path: `/dxpweb/${businessUnit}/${label}/customers/${customerId}/documents/${documentId}`,
      errors: {
        400: 'If backend returns an error or input is invalid.',
        404: 'If the document cannot be found by the given input.',
      },
    },
    requestConfig,
  );
}

type GetCustomerTarcomDocuments = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
  accountId: number;
  type?: DC_Domain_Models_Documents_DocumentType;
  limit?: number;
  offset?: number;
};
/**
 * Gets the customer tarcom documents.
 *
 * @returns ResponseModels_Documents_CustomerDocumentResponse Success
 */
export function getCustomerTarcomDocuments(
  { businessUnit, label, customerId, accountId, type, limit, offset }: GetCustomerTarcomDocuments,
  requestConfig: ApiRequestConfig = {},
): Promise<ResponseModels_Documents_CustomerDocumentResponse> {
  return request(
    {
      method: 'GET',
      path: `/dxpweb/${businessUnit}/${label}/customers/${customerId}/accounts/${accountId}/documents`,
      query: { type, limit, offset },
      errors: { 400: 'Bad Request', 404: 'Not Found' },
    },
    requestConfig,
  );
}

type DownloadCustomerTarcomDocument = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
  accountId: number;
  documentId: string;
};
/**
 * Downloads the customer tarcom document.
 *
 * @returns Blob Success
 */
export function downloadCustomerTarcomDocument(
  { businessUnit, label, customerId, accountId, documentId }: DownloadCustomerTarcomDocument,
  requestConfig: ApiRequestConfig = {},
): Promise<Blob> {
  return request(
    {
      method: 'GET',
      path: `/dxpweb/${businessUnit}/${label}/customers/${customerId}/accounts/${accountId}/documents/${documentId}`,
      errors: { 400: 'Bad Request', 404: 'Not Found' },
    },
    requestConfig,
  );
}

type DownloadDocumentByDocumentId = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
  documentId?: string;
};
/**
 * Download a customer's document based on the provided document ID.
 *
 * @returns Blob Success
 */
export function downloadDocumentByDocumentId(
  { businessUnit, label, customerId, documentId }: DownloadDocumentByDocumentId,
  requestConfig: ApiRequestConfig = {},
): Promise<Blob> {
  return request(
    {
      method: 'GET',
      path: `/dxpweb/${businessUnit}/${label}/customers/${customerId}/documents`,
      query: { documentId },
      errors: { 400: 'Bad Request', 404: 'Not Found' },
    },
    requestConfig,
  );
}

type GetCustomerContracts = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
  accountId: number;
  includePastContracts?: boolean;
  includeActiveContracts?: boolean;
  includeFutureContracts?: boolean;
};
/**
 * Get customer's contracts for the requested contract states
 *
 * @returns ResponseModels_Documents_ContractResponseModel Success
 */
export function getCustomerContracts(
  {
    businessUnit,
    label,
    customerId,
    accountId,
    includePastContracts,
    includeActiveContracts,
    includeFutureContracts,
  }: GetCustomerContracts,
  requestConfig: ApiRequestConfig = {},
): Promise<ResponseModels_Documents_ContractResponseModel> {
  return request(
    {
      method: 'GET',
      path: `/dxpweb/${businessUnit}/${label}/customers/${customerId}/accounts/${accountId}/documents/contracts`,
      query: { includePastContracts, includeActiveContracts, includeFutureContracts },
      errors: { 400: 'Bad Request' },
    },
    requestConfig,
  );
}

type GetLatestCustomerContract = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
};
/**
 * Get latest customer contract, useful for new customers
 *
 * @returns ResponseModels_Documents_ContractResponseModel Success
 */
export function getLatestCustomerContract(
  { businessUnit, label, customerId }: GetLatestCustomerContract,
  requestConfig: ApiRequestConfig = {},
): Promise<ResponseModels_Documents_ContractResponseModel> {
  return request(
    {
      method: 'GET',
      path: `/dxpweb/${businessUnit}/${label}/customers/${customerId}/documents/contracts/latest`,
      errors: { 400: 'Bad Request' },
    },
    requestConfig,
  );
}
